import {
  FMFolderType,
  useFMFolderBreadcrumbsQuery,
  type NWSliderEditSlideMetaFragment,
} from "@/graphql";
import { computed, type ComputedRef } from "vue";
import { useFMFolderNames } from "./FMFolderHelper";

export function getFolderFileIcon(mimeType?: string) {
  if (mimeType == null) {
    return "fas fa file-unknown";
  }

  const parts = mimeType.split("/");

  switch (parts[0]) {
    case "application": {
      switch (parts[1]) {
        case "pdf":
          return "fas fa-file-pdf";
        case "json":
          return "fa-brands fa-square-js";
        default:
          return "fas fa-file-circle-question";
      }
    }
    case "video":
      return "fas fa-file-video";
    case "image":
      return "fas fa-file-image";
    case "audio":
      return "fas fa-file-audio";
    case "text":
      return "fas fa-file-lines";
    case FOLDER_MIME_TYPE:
      return "fas fa-folder";
    default:
      return "fas fa-file-circle-question";
  }
}

export type FMSliderFileItem = {
  fileUUID?: string;
  name: string;
  mimeType: string;
  hasThumbnail: boolean;
  visibleTimeInSeconds: number;
  meta?: NWSliderEditSlideMetaFragment;
};

export const FOLDER_MIME_TYPE = "0_folder";

export function useFolderPath(folderIds: ComputedRef<number[] | number | undefined>) {
  const options = computed(() => ({
    enabled:
      folderIds.value != null &&
      (Array.isArray(folderIds.value) ? folderIds.value.length > 0 : true),
    fetchPolicy: "cache-first" as const,
  }));
  const { result: folderContentResult, loading: loadingBreadcrumbs } = useFMFolderBreadcrumbsQuery(
    computed(() => ({
      ids: folderIds.value!,
    })),
    options,
  );

  const { loading: loadingFolderNames, getFolderStats } = useFMFolderNames();

  const existingElementsTreeMap = computed(
    () =>
      new Map(
        folderContentResult.value?.FMFolderParents.map((node) => {
          const folderStats = getFolderStats(node);
          return [String(node.path), { idAsString: String(node.id), name: folderStats.name }];
        }),
      ),
  );

  const existingElementsIDPathMap = computed(
    () =>
      new Map(
        folderContentResult.value?.FMFolderParents.map((node) => [
          String(node.id),
          String(node.path),
        ]) ?? [],
      ),
  );

  const loading = computed(() => loadingBreadcrumbs.value || loadingFolderNames.value);

  return {
    loading,
    existingElementsIDPathMap,
    existingElementsTreeMap,
  };
}

export type FolderTableContent = {
  key: string;
  mimeType: string;
  name: string;
  fileUUID?: string;
  folderId?: number;
  fileId?: number;
  hasThumbnail: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  createdAtISO: string;
  leaf: boolean;
  folderType: FMFolderType | undefined;
  folderPath?: string;
  siteId?: number;
  labelPath?: string;
  containingFolderId?: number;
  originalFileEnding?: string;
};

export function isZoomAllowed(file: { mimeType: string } | null | undefined): boolean {
  if (file == null) {
    return true;
  }
  const mimeType = file.mimeType;
  if (mimeType.startsWith("text/")) {
    return false;
  }
  if (mimeType === "application/pdf") {
    return false;
  }
  if (mimeType === "application/json") {
    return false;
  }

  return true;
}
